// @owners { team: patient-health }
import { endOfDay, isWithinInterval, startOfDay } from 'date-fns';
import { occurrencesForDate } from './Occurrence';
import { makeOccurrenceID } from './helper';
import { type MedicationTakenEvent, type ReminderSequence } from '~shared/types/clients';
import { type MedicationTakenEventParts, type PendingMedicationTakenEvent } from '~shared/types/medReminders';

/**
 * A taken or not taken occurrence.
 */
class Dose {
  reminderSequenceID: number;

  duration: number;

  gcnSeqno: string | null | undefined;

  ndc: string | null | undefined;

  occurrenceID: string;

  quantity: number;

  start: Date;

  takenAt: Date | null | undefined;

  constructor(
    reminderSequenceID: number,
    duration: number,
    gcnSeqno: string | null | undefined,
    ndc: string | null | undefined,
    quantity: number,
    start: Date,
    takenAt: Date | null | undefined,
  ) {
    this.reminderSequenceID = reminderSequenceID;
    this.duration = duration;
    this.gcnSeqno = gcnSeqno;
    this.ndc = ndc;
    this.occurrenceID = makeOccurrenceID(reminderSequenceID, start, duration, gcnSeqno, ndc);
    this.quantity = quantity;
    this.start = start;
    this.takenAt = takenAt;
  }

  taken(): boolean {
    return !!this.takenAt;
  }

  asTakenEvent(takenAt?: Date): MedicationTakenEventParts {
    return {
      reminder_sequence_id: this.reminderSequenceID,
      gcn_seqno: this.gcnSeqno,
      ndc: this.ndc,
      start: this.start.toString(),
      duration: this.duration,
      quantity: this.quantity,
      taken_at: (takenAt || this.takenAt || new Date()).toString(),
    };
  }

  static fromTakenEvent(event: PendingMedicationTakenEvent | MedicationTakenEvent): Dose {
    return new Dose(
      event.reminder_sequence_id,
      event.duration,
      event.gcn_seqno,
      event.ndc,
      event.quantity,
      new Date(event.start),
      new Date(event.taken_at),
    );
  }
}

function filterStartOnDay(date: Date): (takenEvent: MedicationTakenEvent) => boolean {
  return function filter(takenEvent) {
    return isWithinInterval(new Date(takenEvent.start), { start: startOfDay(date), end: endOfDay(date) });
  };
}

function dosesForDate(sequences: ReminderSequence[], takenEvents: MedicationTakenEvent[], date: Date): Dose[] {
  const dosesByReminderSequenceID: Record<string, Dose> = {};

  occurrencesForDate(sequences, date).forEach((occurrence) => {
    dosesByReminderSequenceID[occurrence.reminderSequenceID] = new Dose(
      occurrence.reminderSequenceID,
      occurrence.duration,
      occurrence.gcnSeqno,
      occurrence.ndc,
      occurrence.quantity,
      occurrence.start,
      null,
    );
  });

  takenEvents.filter(filterStartOnDay(date)).forEach((event) => {
    const dose = Dose.fromTakenEvent(event);
    if (dosesByReminderSequenceID[event.reminder_sequence_id]) {
      dosesByReminderSequenceID[event.reminder_sequence_id].takenAt = dose.takenAt;
    } else {
      dosesByReminderSequenceID[event.reminder_sequence_id] = dose;
    }
  });

  return Object.values(dosesByReminderSequenceID);
}

export { Dose, dosesForDate };
